// Libraries.
import { takeEvery } from 'redux-saga/effects'
import { eventChannel, END } from 'redux-saga'
import { trackActions } from '../interface/actions'
import { forwardAction } from '../../common/utils'

/**
 * Sets up event listeners for a Track's events.
 * The events are turned into actions and dispatched
 *    to be handled by redux elsewhere.
 * @method watchTrackEvents
 * @param  {Object} track A Track object.
 * @param  {Object} webRTC The webRTC stack.
 */
export default function * watchTrackEvents (track, webRTC) {
  /**
   * Create an event channel between the Track and redux-saga.
   * This "forces" the Track events into a redux-saga context.
   */
  const trackChannel = eventChannel(emit => {
    return setListeners(track, emit, END)
  })
  yield takeEvery(trackChannel, forwardAction)
}

/**
 * Function to set-up listeners for a Track object's events.
 * Contains the logic for how a webRTC event should be parsed
 *    before being sent elsewhere. Split into a stand-alone
 *    function in order to be re-usable.
 * @method setListeners
 * @param  {Object}   track A Track object.
 * @param  {Function} emit  Function that receives the parsed events.
 * @param  {Any}      [END='END'] The "end of lifetime" symbol.
 * @return {Function} The unsubscribe function.
 */
export function setListeners (track, emit, END = 'END') {
  // Track ended
  const trackEnded = trackData => {
    /**
     * When the track ends, its channel should be ended (via `END`).
     * The trackManager channel will handle updating state for this event.
     */
    emit(END)
  }

  // The track source (which affected the track identified by trackId) was muted.
  // An example of a track source is a physical media device such as:
  // microphone or camera.
  const trackSourceMuted = trackData => {
    emit(trackActions.trackSourceMuted([trackData.trackId], { mediaId: trackData.mediaId, isLocal: trackData.isLocal }))
  }

  // The track source (which affected the track identified by trackId) was unmuted.
  const trackSourceUnmuted = trackData => {
    emit(
      trackActions.trackSourceUnmuted([trackData.trackId], { mediaId: trackData.mediaId, isLocal: trackData.isLocal })
    )
  }

  track.on('ended', trackEnded)
  track.on('muted', trackSourceMuted)
  track.on('unmuted', trackSourceUnmuted)

  const unsubscribe = () => {
    track.off('ended', trackEnded)
    track.off('muted', trackSourceMuted)
    track.off('unmuted', trackSourceUnmuted)
  }
  return unsubscribe
}
